/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState } from "react";
import { Check } from "react-feather";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from "../../../store/store";
import { REQUIRED_VALIDATION } from "../../../helpers/utils/formUtils";
import { getBase64, getFileExtension } from "../../../helpers/utils/FileHelper";
import TableHeadingTitle from "../../../shared/TableHeadingTitle/TableHeadingTitle";
import SearchBar from "../../../shared/SearchBar/SearchBar";
import { PrimaryButton } from "../../../shared/Buttons/Buttons";
import Modal, { ModalBody, ModalHeader } from "../../../shared/Modals/Modal";
import TextField from "../../../shared/Inputs/TextField/TextField";
import TextArea from "../../../shared/Inputs/TextArea/TextArea";
import ErrorText from "../../../shared/Inputs/ErrorText/ErrorText";
import {
  closeEditModal,
  FILTER_ALL,
  FILTER_PROGRAM_TESTIMONIALS,
  FILTER_SUCCESS_STORIES,
  setFilter,
  setSearch,
} from "../../../store/features/AdminPanel/SuccessStories/successStoriesSlice";
import {
  addSuccessStory,
  updateSuccessStory,
} from "../../../store/features/AdminPanel/SuccessStories/successStoriesApi";
import "./successStories.scss";

const FILTER_OPTIONS = [
  { value: FILTER_ALL, label: "All" },
  { value: FILTER_SUCCESS_STORIES, label: "Success Stories" },
  { value: FILTER_PROGRAM_TESTIMONIALS, label: "Program Testimonials" },
];

const SuccessStoriesHeader = () => {
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [avatarBase64, setAvatarBase64] = useState<string>("");
  const [avatarExt, setAvatarExt] = useState<string>("");
  const [avatarPreview, setAvatarPreview] = useState<string>("");
  const [avatarError, setAvatarError] = useState<string>("");
  const [pageError, setPageError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useAppDispatch();
  const state: any = useAppSelector((s: RootState) => s.SuccessStoriesSlice);

  const validationSchema = yup.object().shape({
    name: yup.string().required(REQUIRED_VALIDATION("Name")),
    quote: yup.string().required(REQUIRED_VALIDATION("Quote")),
    role: yup.string().nullable().max(191),
    youtube_url: yup
      .string()
      .nullable()
      .transform((v) => (v === "" ? null : v))
      .matches(
        /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$|^(https?:\/\/)?[\w-]+\.(wistia\.com|wi\.st)\/.+$|^https?:\/\/fast\.wistia\.net\/.+$/i,
        { message: "Must be a valid YouTube or Wistia URL", excludeEmptyString: true }
      ),
    tags: yup.string().nullable().max(500),
    achievement_1: yup.string().nullable().max(191),
    achievement_2: yup.string().nullable().max(191),
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(validationSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      show_on_success_stories: true,
      show_on_program_testimonials: false,
    },
  });

  const clearForm = () => {
    reset({
      name: "",
      role: "",
      quote: "",
      youtube_url: "",
      achievement_1: "",
      achievement_2: "",
      tags: "",
      show_on_success_stories: true,
      show_on_program_testimonials: false,
    });
    setAvatarBase64("");
    setAvatarExt("");
    setAvatarPreview("");
    setAvatarError("");
    setPageError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const closeModal = () => {
    setShowModal(false);
    dispatch(closeEditModal());
    clearForm();
  };

  const onAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError("Image must be less than 2 MB");
      return;
    }
    const ext = getFileExtension(file.name);
    const preview = URL.createObjectURL(file);
    const base64 = (await getBase64(file)) as string;
    setAvatarExt(ext);
    setAvatarPreview(preview);
    setAvatarBase64(base64);
    setAvatarError("");
  };

  const removeAvatar = () => {
    setAvatarBase64("");
    setAvatarExt("");
    setAvatarPreview("");
    setAvatarError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onSubmit = (values: any) => {
    if (
      !values.show_on_success_stories &&
      !values.show_on_program_testimonials
    ) {
      setPageError("Select at least one page.");
      return;
    }
    setPageError("");

    const payload: any = {
      name: values.name,
      role: values.role || null,
      quote: values.quote,
      youtube_url: values.youtube_url || null,
      tags: values.tags || null,
      show_on_success_stories: values.show_on_success_stories ? 1 : 0,
      show_on_program_testimonials: values.show_on_program_testimonials ? 1 : 0,
      status: state.single?.status !== undefined ? state.single.status : 1,
    };

    const achievements: { text: string }[] = [];
    if (values.achievement_1) achievements.push({ text: values.achievement_1 });
    if (values.achievement_2) achievements.push({ text: values.achievement_2 });
    payload.achievements = achievements;

    if (avatarBase64) {
      payload.avatar = avatarBase64;
      payload.extension = avatarExt;
    }

    if (state.edit && state.single?.id) {
      dispatch(updateSuccessStory({ id: state.single.id, data: payload }));
    } else {
      dispatch(addSuccessStory(payload));
    }
  };

  useEffect(() => {
    if (state.req_success.add) closeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.req_success.add]);

  useEffect(() => {
    if (state.edit && state.single) {
      setShowModal(true);
      setValue("name", state.single.name || "");
      setValue("role", state.single.role || "");
      setValue("quote", state.single.quote || "");
      setValue("youtube_url", state.single.youtube_url || "");
      setValue("tags", state.single.tags || "");
      setValue("achievement_1", state.single.achievements?.[0]?.text || "");
      setValue("achievement_2", state.single.achievements?.[1]?.text || "");
      setValue(
        "show_on_success_stories",
        !!state.single.show_on_success_stories
      );
      setValue(
        "show_on_program_testimonials",
        !!state.single.show_on_program_testimonials
      );
      setAvatarPreview(state.single.avatar || "");
      setAvatarBase64("");
      setAvatarExt("");
      setAvatarError("");
      setPageError("");
    }
  }, [state.edit, state.single, setValue]);

  useEffect(() => {
    dispatch(setSearch(searchText));
  }, [searchText, dispatch]);

  return (
    <div className="success-stories-header">
      <div className="left__section">
        <TableHeadingTitle>Our Stories</TableHeadingTitle>
      </div>

      <div className="right__section">
        <div className="ss-filter">
          <select
            aria-label="Filter stories"
            value={state.filter}
            onChange={(e) => dispatch(setFilter(e.target.value as any))}
          >
            {FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="search">
          <SearchBar value={searchText} setValue={setSearchText} />
        </div>
        <div className="button__witdh">
          <PrimaryButton
            text="Add New"
            size="sm"
            type="button"
            loading={false}
            onClick={() => {
              clearForm();
              setShowModal(true);
            }}
          />
        </div>
      </div>

      <Modal show={showModal} setShow={setShowModal} onClick={closeModal}>
        <ModalHeader title={state.edit ? "Update Story" : "Add Story"} />
        <ModalBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="ss-name-avatar-row">
              <div className="ss-avatar-col">
                <div className="ss-avatar-row">
                  <img
                    className="ss-avatar-preview"
                    src={
                      avatarPreview ||
                      "https://placehold.co/100x100?text=No+Image"
                    }
                    alt="avatar"
                  />
                  <div className="ss-avatar-actions">
                    <button
                      type="button"
                      className="ss-avatar-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload avatar
                    </button>
                    {avatarPreview && (
                      <button
                        type="button"
                        className="ss-avatar-btn ss-avatar-btn--remove"
                        onClick={removeAvatar}
                      >
                        Remove
                      </button>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={onAvatarChange}
                      style={{ display: "none" }}
                    />
                    <ErrorText error={avatarError} />
                  </div>
                </div>
              </div>
              <div className="ss-name-col">
                <TextField
                  type="text"
                  label="Name"
                  name="name"
                  register={register}
                  error={errors?.name}
                />
              </div>
            </div>

            <TextField
              type="text"
              label="Role (e.g. Product Manager • Tech Startup)"
              name="role"
              register={register}
              error={errors?.role}
            />

            <TextArea
              label="Quote"
              name="quote"
              register={register}
              error={errors?.quote}
            />

            <TextField
              type="text"
              label="Video URL — YouTube or Wistia (optional)"
              name="youtube_url"
              register={register}
              error={errors?.youtube_url}
              placeholder="https://www.youtube.com/watch?v=... or https://account.wistia.com/medias/..."
            />

            <div className="ss-page-toggles">
              <span className="ss-page-toggles-label">Show on</span>
              <div className="ss-chip-row">
                <button
                  type="button"
                  className={`ss-chip ${
                    watch("show_on_success_stories") ? "ss-chip--active" : ""
                  }`}
                  onClick={() =>
                    setValue(
                      "show_on_success_stories",
                      !watch("show_on_success_stories"),
                      { shouldDirty: true, shouldValidate: true }
                    )
                  }
                  aria-pressed={!!watch("show_on_success_stories")}
                >
                  {watch("show_on_success_stories") && (
                    <Check className="ss-chip-icon" size={14} />
                  )}
                  Success Stories
                </button>
                <button
                  type="button"
                  className={`ss-chip ${
                    watch("show_on_program_testimonials")
                      ? "ss-chip--active"
                      : ""
                  }`}
                  onClick={() =>
                    setValue(
                      "show_on_program_testimonials",
                      !watch("show_on_program_testimonials"),
                      { shouldDirty: true, shouldValidate: true }
                    )
                  }
                  aria-pressed={!!watch("show_on_program_testimonials")}
                >
                  {watch("show_on_program_testimonials") && (
                    <Check className="ss-chip-icon" size={14} />
                  )}
                  Program Testimonials
                </button>
              </div>
              <input type="hidden" {...register("show_on_success_stories")} />
              <input
                type="hidden"
                {...register("show_on_program_testimonials")}
              />
              <ErrorText error={pageError} />
            </div>

            {watch("show_on_success_stories") && (
              <div className="ss-two-col">
                <TextField
                  type="text"
                  label="Achievement 1 (↑ icon)"
                  name="achievement_1"
                  register={register}
                  error={errors?.achievement_1}
                  placeholder="e.g. Career switcher"
                />
                <TextField
                  type="text"
                  label="Achievement 2 (✓ icon)"
                  name="achievement_2"
                  register={register}
                  error={errors?.achievement_2}
                  placeholder="e.g. Multiple offers"
                />
              </div>
            )}

            {watch("show_on_program_testimonials") && (
              <TextField
                type="text"
                label="Tags (comma-separated)"
                name="tags"
                register={register}
                error={errors?.tags}
                placeholder="Hands-on Learning, Mentorship"
              />
            )}

            <div className="button__witdh mt-2">
              <PrimaryButton
                text={state.edit ? "Update" : "Add"}
                type="submit"
                loading={state.loading.add}
              />
            </div>
          </form>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default SuccessStoriesHeader;
